/**
 * WorkSignInComposer — the secure sign-in form that takes the composer's place
 * when a run hits a login wall.
 *
 * The agent cannot type a password (it never gets one), and the user should
 * never paste one into the chat, where it would live in the transcript forever.
 * So the ask surfaces here instead: the form names the site the browser is
 * ACTUALLY on, collects only the boxes that page actually has, and hands the
 * values to the remote browser, which types them into the page.
 *
 * Those values are the one thing in this app that must leave no trace. They
 * live in this component's state until submit, are dropped the moment they are
 * sent, and are never logged, never put in an event or a message, and never
 * shown to the model. Nothing here may console.log a value or hand one to a
 * store — see coworkerApi.submitBrowserLogin.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Pressable, StyleSheet, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useCoworkerStore, useToastStore } from '@/store';
import { coworkerApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import { workHostOf } from '@/constants/work';

// What each detected box is called and how it should behave. The page's own
// label wins when it has one — a site that says "Gab handle" should not be
// relabelled "Username" underneath the user.
const FIELD_KINDS = {
  email: { label: 'Email address', keyboardType: 'email-address' },
  username: { label: 'Username', keyboardType: 'default' },
  phone: { label: 'Phone number', keyboardType: 'phone-pad' },
  password: { label: 'Password', secure: true },
  new_password: { label: 'New password', secure: true },
  code: { label: 'Verification code', keyboardType: 'number-pad' },
  text: { label: 'Sign-in details', keyboardType: 'default' },
};

export default function WorkSignInComposer({ taskId, loginRequest }) {
  const { colors } = useTheme();
  const openBrowserViewer = useCoworkerStore((s) => s.openBrowserViewer);
  const { addToast } = useToastStore((s) => s.actions);
  const [values, setValues] = useState({});
  const [revealed, setRevealed] = useState({});
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef({});

  // One box per thing the page needs, identified by what it is for — that is
  // also how the value travels back, so the browser can re-find the box.
  const fields = useMemo(() => {
    const list = Array.isArray(loginRequest?.fields) ? loginRequest.fields : [];
    return list
      .filter((f) => f && FIELD_KINDS[f.kind])
      .map((f) => ({ ...f, label: f.label || FIELD_KINDS[f.kind].label }));
  }, [loginRequest?.fields]);

  // Every fresh ask — a 2FA code step, or a retry after a wrong password —
  // arrives as a new event, and starts from empty boxes.
  useEffect(() => {
    setValues({});
    setRevealed({});
    setError(loginRequest?.error || '');
  }, [loginRequest?.seq, loginRequest?.error]);

  const isCodeStep = fields.length > 0 && fields.every((f) => f.kind === 'code');
  const canSubmit = fields.length > 0 && fields.every((f) => (values[f.kind] || '').trim().length > 0);

  const handleSubmit = useCallback(async () => {
    if (isBusy || !canSubmit) return;
    setIsBusy(true);
    setError('');
    const payload = fields.map((f) => ({ kind: f.kind, text: values[f.kind] }));
    try {
      const out = await coworkerApi.submitBrowserLogin(taskId, {
        id: loginRequest?.id,
        values: payload,
      });
      // Held no longer than it takes to send.
      setValues({});
      setRevealed({});
      if (out?.stage === 'retry') setError('That did not go through. Check the details and try again.');
      else if (out?.stage === 'complete') addToast('Signed in — Work is carrying on', 'success');
    } catch (err) {
      setError(err?.message || 'Could not reach the browser. Try again.');
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, canSubmit, fields, values, taskId, loginRequest?.id, addToast]);

  // Not every ask is worth an account. Skipping releases the run immediately
  // instead of leaving it parked at the login wall until it times out.
  const handleSkip = useCallback(async () => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await coworkerApi.submitBrowserLogin(taskId, { id: loginRequest?.id, skip: true });
      setValues({});
    } catch (err) {
      addToast(err?.message || 'Could not skip the sign-in', 'error');
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, taskId, loginRequest?.id, addToast]);

  // Anything the form cannot ask for — a captcha, an SSO redirect, a passkey —
  // the user can still do by hand in the live browser.
  const handleOpenBrowser = useCallback(() => {
    setValues({});
    openBrowserViewer(taskId);
  }, [openBrowserViewer, taskId]);

  if (!taskId || !loginRequest) return null;

  const url = typeof loginRequest.url === 'string' ? loginRequest.url : '';
  const host = url ? workHostOf(url) : '';
  // The agent asked for one page and the browser is on another: worth showing,
  // because that gap is what a hand-off to a lookalike page looks like.
  const claimed = loginRequest.requestedUrl ? workHostOf(loginRequest.requestedUrl) : '';
  const redirected = !!claimed && !!host && claimed !== host;

  return (
    <View
      style={[styles.sheet, { backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color_secondary }]}
      accessibilityLabel={host ? `Sign in to ${host} to continue` : 'Sign in to continue'}
    >
      <View style={styles.header}>
        <Icon name="user-lock" size={15} color={colors.text_color_secondary} />
        <Text size="m" weight="semibold" style={styles.title} numberOfLines={1}>
          {isCodeStep ? 'Enter your verification code' : 'Sign in to continue'}
        </Text>
        <View style={styles.site}>
          <Icon name="lock" size={11} color={colors.text_color_tertiary} />
          <Text size="xs" color="tertiary" numberOfLines={1}>{host || 'the current page'}</Text>
        </View>
      </View>

      {!!loginRequest.reason && !isCodeStep && (
        <Text size="xs" color="secondary" numberOfLines={2}>{loginRequest.reason}</Text>
      )}

      {redirected && (
        <Text size="xxs" color="danger" numberOfLines={2}>
          {`Work asked for ${claimed} and the browser is on ${host}. Open the page to check it before signing in.`}
        </Text>
      )}

      {fields.length > 0 ? (
        <View style={styles.fields}>
          {fields.map((field, index) => {
            const kind = FIELD_KINDS[field.kind];
            const isSecret = !!kind.secure && !revealed[field.kind];
            const isLast = index === fields.length - 1;
            return (
              <View key={field.kind} style={styles.fieldRow}>
                <View style={styles.fieldInput}>
                  <FloatingLabelInput
                    ref={(node) => { inputRefs.current[field.kind] = node; }}
                    label={field.label}
                    value={values[field.kind] || ''}
                    onChangeText={(text) => setValues((prev) => ({ ...prev, [field.kind]: text }))}
                    secureTextEntry={isSecret}
                    keyboardType={kind.keyboardType || 'default'}
                    isFullWidth
                    style={{ backgroundColor: colors.solid_color_primary }}
                    returnKeyType={isLast ? 'go' : 'next'}
                    onSubmitEditing={() => {
                      if (isLast) handleSubmit();
                      else inputRefs.current[fields[index + 1]?.kind]?.focus?.();
                    }}
                    disabled={isBusy}
                  />
                </View>
                {!!kind.secure && (
                  <Pressable
                    onPress={() => setRevealed((prev) => ({ ...prev, [field.kind]: !prev[field.kind] }))}
                    hitSlop={8}
                    accessibilityRole="button"
                    accessibilityLabel={isSecret ? 'Show password' : 'Hide password'}
                    style={styles.reveal}
                  >
                    <Icon name={isSecret ? 'eye' : 'eye-slash'} size={14} color={colors.text_color_tertiary} />
                  </Pressable>
                )}
              </View>
            );
          })}
        </View>
      ) : (
        <Text size="xs" color="secondary">
          This page does not have a sign-in form Work can fill for you. Open the browser and sign in there.
        </Text>
      )}

      {!!error && <Text size="xxs" color="danger" numberOfLines={2}>{error}</Text>}

      <Text size="xxs" color="tertiary" numberOfLines={3}>
        {isCodeStep
          ? 'The code goes straight to the browser doing the work. It is not saved and Work never sees it.'
          : 'This goes straight to the browser doing the work, which stays signed in for later tasks. Your password is not saved, and neither Work nor this chat ever sees it.'}
      </Text>

      <View style={styles.actions}>
        <Pressable
          onPress={handleOpenBrowser}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Sign in on the web page instead"
          style={styles.link}
        >
          <Text size="xs" color="secondary" style={styles.linkText}>Sign in on web page instead</Text>
        </Pressable>
        <View style={styles.buttons}>
          <Button text="Skip" size="small" variant="secondary" onPress={handleSkip} isDisabled={isBusy} />
          {fields.length > 0 && (
            <Button
              text={isCodeStep ? 'Submit code' : 'Sign in'}
              size="small"
              onPress={handleSubmit}
              isDisabled={isBusy || !canSubmit}
              isLoading={isBusy}
            />
          )}
        </View>
      </View>
    </View>
  );
}

WorkSignInComposer.propTypes = {
  taskId: PropTypes.string,
  loginRequest: PropTypes.object,
};

const styles = StyleSheet.create({
  sheet: {
    gap: 10,
    borderWidth: 1,
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    flex: 1,
  },
  site: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    maxWidth: '45%',
  },
  fields: {
    gap: 14,
    paddingTop: 4,
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  fieldInput: {
    flex: 1,
  },
  reveal: {
    padding: 6,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  link: {
    flexShrink: 1,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  linkText: {
    textDecorationLine: 'underline',
  },
  buttons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto',
  },
});
